import { useState, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/Bits';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Key01Icon,
  PencilEdit01Icon,
  Cancel01Icon,
  Tick02Icon,
  LockPasswordIcon,
  SecurityLockIcon,
} from '@hugeicons/core-free-icons';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ROLE_LABEL } from '@/lib/constants';
import { ButtonGroup } from '@/components/ui/button-group';

// Microsoft logo (4-color squares)
function MicrosoftLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

const ROLES = ['admin', 'evaluador', 'comite', 'lector'];

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * UserCrudPanel — Panel unificado de gestión de usuarios para el tab Usuarios del admin.
 * Incluye filtros avanzados, tabla con avatar, badges, selección bulk, cambio de rol inline,
 * y acciones: crear, editar, desactivar/reactivar, restablecer contraseña, ver permisos.
 *
 * @param {{ users: Array, onRefresh: () => void, currentUserId?: string, selectedUserIds?: Array, onSelectUser?: Function, onSelectAll?: Function, isAllSelected?: boolean, onChangeRole?: Function, onViewPermissions?: Function }} props
 */
export function UserCrudPanel({
  users,
  onRefresh,
  currentUserId,
  selectedUserIds = [],
  onSelectUser,
  onSelectAll,
  isAllSelected = false,
  onChangeRole,
  onViewPermissions,
}) {
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAuthProvider, setFilterAuthProvider] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');

  // Derive unique departments from users
  const departments = [...new Set(users.map((u) => u.department).filter(Boolean))].sort();

  // Filtered users
  const filteredUsers = users.filter((u) => {
    // Text search: name or email
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesName = u.name?.toLowerCase().includes(q);
      const matchesEmail = u.email?.toLowerCase().includes(q);
      if (!matchesName && !matchesEmail) return false;
    }
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    if (filterAuthProvider !== 'all' && u.auth_provider !== filterAuthProvider) return false;
    if (filterDepartment !== 'all' && u.department !== filterDepartment) return false;
    return true;
  });

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    filterRole !== 'all' ||
    filterStatus !== 'all' ||
    filterAuthProvider !== 'all' ||
    filterDepartment !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setFilterRole('all');
    setFilterStatus('all');
    setFilterAuthProvider('all');
    setFilterDepartment('all');
  };

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    role: 'lector',
    department: '',
    title: '',
    password: '',
  });
  const [createErrors, setCreateErrors] = useState({});

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'lector',
    department: '',
    title: '',
  });
  const [editErrors, setEditErrors] = useState({});

  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // --- Create User ---
  const openCreate = () => {
    setCreateForm({ name: '', email: '', role: 'lector', department: '', title: '', password: '' });
    setCreateErrors({});
    setCreateOpen(true);
  };

  const validateCreate = () => {
    const errors = {};
    if (!createForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!createForm.email.trim()) errors.email = 'El email es requerido';
    else if (!validateEmail(createForm.email)) errors.email = 'Email inválido';
    if (!createForm.password || createForm.password.length < 8)
      errors.password = 'La contraseña debe tener al menos 8 caracteres';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setSubmitting(true);
    try {
      await api.post('/admin/users', {
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        role: createForm.role,
        department: createForm.department.trim(),
        title: createForm.title.trim(),
        password: createForm.password,
      });
      toast.success('Usuario creado exitosamente');
      setCreateOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Edit User ---
  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'lector',
      department: user.department || '',
      title: user.title || '',
    });
    setEditErrors({});
    setEditOpen(true);
  };

  const validateEdit = () => {
    const errors = {};
    if (!editForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!editForm.email.trim()) errors.email = 'El email es requerido';
    else if (!validateEmail(editForm.email)) errors.email = 'Email inválido';
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async () => {
    if (!validateEdit()) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
        department: editForm.department.trim(),
        title: editForm.title.trim(),
      });
      toast.success('Usuario actualizado exitosamente');
      setEditOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Toggle Status ---
  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/admin/users/${user.id}/status`, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Usuario reactivado' : 'Usuario desactivado');
      onRefresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // --- Reset Password ---
  const openPasswordReset = (userId) => {
    setPasswordUserId(userId);
    setNewPassword('');
    setPasswordError('');
    setPasswordOpen(true);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${passwordUserId}/password`, { password: newPassword });
      toast.success('Contraseña restablecida exitosamente');
      setPasswordOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header with Create button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Gestión de usuarios</h3>
        <Button size="sm" onClick={openCreate} data-testid="btn-crear-usuario">
          Crear usuario
        </Button>
      </div>

      {/* Advanced Filters */}
      <div className="mb-4 space-y-3">
        {/* Search bar */}
        <div className="relative">
          <Input
            type="search"
            placeholder="Buscar por nombre o correo electrónico..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 pl-10"
            aria-label="Buscar usuarios por nombre o email"
            data-testid="user-search-input"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[140px] h-9 text-xs" aria-label="Filtrar por rol">
              <SelectValue placeholder="Rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {ROLE_LABEL[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-9 text-xs" aria-label="Filtrar por estado">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="inactive">Inactivo</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterAuthProvider} onValueChange={setFilterAuthProvider}>
            <SelectTrigger className="w-[150px] h-9 text-xs" aria-label="Filtrar por autenticación">
              <SelectValue placeholder="Autenticación" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="azure_ad">Microsoft</SelectItem>
            </SelectContent>
          </Select>

          {departments.length > 0 && (
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-[160px] h-9 text-xs" aria-label="Filtrar por departamento">
                <SelectValue placeholder="Departamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearFilters}
              data-testid="clear-filters-btn">
              Limpiar filtros
            </Button>
          )}

          <span className="ml-auto text-xs text-muted-foreground">
            {filteredUsers.length} de {users.length} usuario{users.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card rounded-xl border border-border shadow-card overflow-x-auto">
        <Table data-testid="user-crud-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {onSelectAll && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={onSelectAll}
                    aria-label="Seleccionar todos los usuarios"
                    data-testid="select-all-users"
                  />
                </TableHead>
              )}
              {['Usuario', 'Rol', 'Departamento', 'Autenticación', 'Estado', 'Acciones'].map((h) => (
                <TableHead key={h} className="text-xs uppercase tracking-wide">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow key={u.id}>
                {onSelectUser && (
                  <TableCell>
                    <Checkbox
                      checked={selectedUserIds.includes(u.id)}
                      onCheckedChange={(checked) => onSelectUser(u.id, checked)}
                      aria-label={`Seleccionar usuario ${u.name}`}
                      data-testid={`select-user-${u.id}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                        {u.initials || u.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      {u.title && <p className="text-[11px] text-muted-foreground/70 truncate">{u.title}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {onChangeRole && currentUserId !== u.id ? (
                    <Select value={u.role} onValueChange={(v) => onChangeRole(u.id, v)}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid={`role-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs font-medium text-primary">
                      {ROLE_LABEL[u.role] || u.role} {currentUserId === u.id ? '(usted)' : ''}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{u.department || '—'}</TableCell>
                <TableCell>
                  {u.auth_provider === 'azure_ad' ? (
                    <div className="flex items-center gap-1.5">
                      <MicrosoftLogo className="h-4 w-4" />
                      <span className="text-xs text-muted-foreground">Microsoft</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <HugeiconsIcon icon={Key01Icon} className="h-3.5 w-3.5" />
                      <span className="text-xs">Local</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge status={u.status === 'active' ? 'Activo' : 'Inactivo'} />
                </TableCell>
                <TableCell>
                  <ButtonGroup aria-label="Acciones de usuario">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() => openEdit(u)}
                      data-testid={`btn-editar-${u.id}`}>
                      <HugeiconsIcon icon={PencilEdit01Icon} className="h-3.5 w-3.5" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-7 gap-1 text-xs ${u.status === 'active' ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                      onClick={() => handleToggleStatus(u)}
                      data-testid={`btn-status-${u.id}`}>
                      <HugeiconsIcon icon={u.status === 'active' ? Cancel01Icon : Tick02Icon} className="h-3.5 w-3.5" />
                      {u.status === 'active' ? 'Desactivar' : 'Reactivar'}
                    </Button>
                    {u.auth_provider === 'local' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={() => openPasswordReset(u.id)}
                        data-testid={`btn-password-${u.id}`}>
                        <HugeiconsIcon icon={LockPasswordIcon} className="h-3.5 w-3.5" />
                        Contraseña
                      </Button>
                    )}
                    {onViewPermissions && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={() => onViewPermissions(u.id)}
                        data-testid={`permissions-btn-${u.id}`}>
                        <HugeiconsIcon icon={SecurityLockIcon} className="h-3.5 w-3.5" />
                        Permisos
                      </Button>
                    )}
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={onSelectUser ? 7 : 6} className="text-center text-sm text-muted-foreground py-8">
                  {hasActiveFilters
                    ? 'No se encontraron usuarios con los filtros aplicados.'
                    : 'No hay usuarios registrados.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="create-user-description">
          <DialogHeader>
            <DialogTitle>Crear usuario</DialogTitle>
            <DialogDescription id="create-user-description">
              Complete los campos para crear un nuevo usuario con autenticación local.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Nombre *</Label>
              <Input
                id="create-name"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre completo"
                aria-invalid={!!createErrors.name}
                aria-describedby={createErrors.name ? 'create-name-error' : undefined}
              />
              {createErrors.name && (
                <p id="create-name-error" className="text-xs text-red-600">
                  {createErrors.name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="usuario@ejemplo.cl"
                aria-invalid={!!createErrors.email}
                aria-describedby={createErrors.email ? 'create-email-error' : undefined}
              />
              {createErrors.email && (
                <p id="create-email-error" className="text-xs text-red-600">
                  {createErrors.email}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Rol</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger id="create-role" aria-label="Seleccionar rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-department">Departamento</Label>
              <Input
                id="create-department"
                value={createForm.department}
                onChange={(e) => setCreateForm((f) => ({ ...f, department: e.target.value }))}
                placeholder="Departamento"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-title">Cargo</Label>
              <Input
                id="create-title"
                value={createForm.title}
                onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Cargo o título"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Contraseña *</Label>
              <Input
                id="create-password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Mínimo 8 caracteres"
                aria-invalid={!!createErrors.password}
                aria-describedby={createErrors.password ? 'create-password-error' : undefined}
              />
              {createErrors.password && (
                <p id="create-password-error" className="text-xs text-red-600">
                  {createErrors.password}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? 'Creando...' : 'Crear usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="edit-user-description">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription id="edit-user-description">
              Modifique los campos del usuario. La contraseña no se puede editar desde aquí.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre completo"
                aria-invalid={!!editErrors.name}
                aria-describedby={editErrors.name ? 'edit-name-error' : undefined}
              />
              {editErrors.name && (
                <p id="edit-name-error" className="text-xs text-red-600">
                  {editErrors.name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="usuario@ejemplo.cl"
                aria-invalid={!!editErrors.email}
                aria-describedby={editErrors.email ? 'edit-email-error' : undefined}
              />
              {editErrors.email && (
                <p id="edit-email-error" className="text-xs text-red-600">
                  {editErrors.email}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger id="edit-role" aria-label="Seleccionar rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-department">Departamento</Label>
              <Input
                id="edit-department"
                value={editForm.department}
                onChange={(e) => setEditForm((f) => ({ ...f, department: e.target.value }))}
                placeholder="Departamento"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-title">Cargo</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Cargo o título"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent className="sm:max-w-sm" aria-describedby="password-reset-description">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogDescription id="password-reset-description">
              Ingrese la nueva contraseña para este usuario.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva contraseña *</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordError('');
                }}
                placeholder="Mínimo 8 caracteres"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'new-password-error' : undefined}
              />
              {passwordError && (
                <p id="new-password-error" className="text-xs text-red-600">
                  {passwordError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handlePasswordReset} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Restablecer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
